import {Fragment, useState} from "react";
import {format} from "date-fns";
import {
    BreadCrumb,
    DropdownAction,
    TablePaging,
    CenteredModal,
    ReactSingleSelect,
    FormInput,
    DropzoneFile, Editor
} from "~/components";
import {faEdit, faEye, faTrash} from "@fortawesome/free-solid-svg-icons";
import {Controller, useForm} from "react-hook-form";
import PrivateBlogPostAPI from '~/api/privateBlogPostApi';
import {toast} from "react-toastify";
import {Plus} from "lucide-react";
import commonApi from "~/api/commonApi";

const breadCrumbItem = [
    { text: "Landing Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Private Blog Post", to: "", isActive: true },
]

function PrivateBlogPost(){
    const [reloadTable, setReloadTable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isAddNew, setIsAddNew] = useState(false);
    const [privateBlogPostDetail, setPrivateBlogPostDetail] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [content, setContent] = useState("");
    const [isViewMode, setIsViewMode] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [id, setId] = useState(0);

    const handleAddNewPrivateBlogPost = () => {
        setIsViewMode(false);
        PrivateBlogPostAPI.getById("0")
            .then(res => {
                setPrivateBlogPostDetail(res.data);
                setIsAddNew(true);
                setShowModal(true);
            })
            .catch(err => {  });
    }

    const tablePagingConfig = {
        columns: [
            {
                name: 'ID',
                width: '40px',
                className: 'fw-bold',
                key: 'PrivateBlogPostId'
            },
            {
                name: 'TYPE',
                width: '100px',
                key: 'Type'
            },
            {
                name: 'TITLE',
                width: '200px',
                key: 'Title'
            },
            {
                name: 'DESCRIPTION',
                width: '300px',
                key: 'Description',
                isMoreLess: true
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                className: 'text-center',
                headerClassName: 'text-center',
                template: (row) => {
                    return (
                        <div className="dropdown d-inline-block">
                            <DropdownAction dropdownItems={[
                                { text: "View Details", icon: faEye, onClick: () => handleEditPrivateBlogPost(row.PrivateBlogPostId, true) },
                                { text: "Edit", icon: faEdit, onClick: () => handleEditPrivateBlogPost(row.PrivateBlogPostId, false) },
                                {text: "Delete", icon: faTrash, onClick: () => {
                                    setId(row.PrivateBlogPostId);
                                    setShowDeleteModal(true);
                                    }}
                            ]} isVerticalIcon={false}/>
                        </div>
                    )
                }
            }
        ],
        url: "/private-blog-posts/paged",
        tableKey: "TablePrivateBlog",
        extraButtons: [
            <button className="btn btn-bt-primary ms-4" type="button" onClick={handleAddNewPrivateBlogPost}>
                Add New Blog Post <Plus size={15}/>
            </button>
        ]
    }

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm();

    const handleEditPrivateBlogPost = (id, viewMode) => {
        setIsViewMode(viewMode);
        PrivateBlogPostAPI.getById(id)
            .then(res => {
                const resultDetails = res.data?.PrivateBlogPost;
                if (resultDetails) {
                    commonApi.getUploadFile(resultDetails.ThumbnailFileName)
                        .then((res) => {
                            const file = new File([res.data], resultDetails.ThumbnailFileName, { type: res.data.type });
                            const preview = URL.createObjectURL(file);
                            const newFile = {
                                name: file.name,
                                size: (file.size / 1024).toFixed(2) + " KB",
                                preview,
                                file,
                            };
                            setSelectedFile(newFile);
                        })
                        .catch(() => {
                        });
                }
                setPrivateBlogPostDetail(res.data);
                const formValues = { ...res.data.PrivateBlogPost };
                formValues.TypeId = res.data.PrivateBlogPost.TypeId
                    ? { value: res.data.PrivateBlogPost.TypeId, label: res.data.PrivateBlogPost.Type}
                    : null;
                reset(formValues);
                setIsAddNew(false);
                setShowModal(true);
            })
            .catch(err => {  });
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        reset({
            Title: "",
            Content: "",
            Description: "",
            RoutePath: "",
            file: null,
            TypeId: null
        })
    }

    const onSubmitFrom = (data) => {
        const { file, TypeId, ...restData } = data;
        const formData = new FormData();
        Object.entries(restData).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append("TypeId", TypeId.value);
        if (file?.[0]) {
            formData.append("file", file[0]);
        }
        if(!isAddNew){
            PrivateBlogPostAPI.update(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your private blog post has been updated.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your private blog post.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while updating your private blog post.</span>)
                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
        else{
            PrivateBlogPostAPI.insert(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your private blog post has been added.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while adding your private blog post.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while adding your private blog post.</span>)
                    
                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
    }

    const handleConfirmDelete = () => {
        PrivateBlogPostAPI.delete(id)
            .then(res => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your private blog post has been deleted.</span>)
                    : toast.error(<span
                        className="fs-9 fw-bold">An error occurred while deleting your private blog post.</span>)
            })
            .catch(err => {
                toast.error(<span
                    className="fs-9 fw-bold">An error occurred while deleting your private blog post.</span>)
                
            })
            .finally(() => {
                showDeleteModal(false);
                setReloadTable(pre => !pre);
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5">Private Blog Post</h2>
            <TablePaging tablePagingConfig={tablePagingConfig} reloadTable={reloadTable}/>
            <CenteredModal show={showModal}
                           onClose={handleCloseModal}
                           title={isViewMode ? "Blog Post Details" : (isAddNew ? "Add Blog Post" : "Edit Blog Post")}
                           modalType="modal-lg"
                           formId="formPrivateBlogPostAction"
                           isViewMode={isViewMode}
                           closeText={isViewMode ? "Close" : "Cancel"}

            >
                <form onSubmit={handleSubmit(onSubmitFrom)} id="formPrivateBlogPostAction">
                    <div className="mb-3">
                        <div className="mb-3">
                            <span className="form-label">Type</span>
                            <Controller
                                name="TypeId"
                                control={control}
                                rules={{
                                    required: 'Type is required.'
                                }}
                                render={({ field }) => (
                                    <ReactSingleSelect
                                        options={privateBlogPostDetail.PrivateBlogPostType}
                                        value={field.value}
                                        onChange={field.onChange}
                                        errors={errors}
                                        name="TypeId"
                                        placeholder="Select type..."
                                        disabled={isViewMode}
                                    />
                                )}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "Title"
                                       errors={errors}
                                       required={true}
                                       label="Assign Title"
                                       validation={{...register("Title", {
                                               required: "Title is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "Description"
                                       textarea={true}
                                       errors={errors}
                                       required={true}
                                       label="Assign Description"
                                       validation={{...register("Description", {
                                               required: "Description is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <FormInput showHeaderLabel={true}
                                       name = "RoutePath"
                                       errors={errors}
                                       required={true}
                                       label="Assign Route Path"
                                       validation={{...register("RoutePath", {
                                               required: "Route Path is required."
                                           })}}
                                       disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <DropzoneFile
                                label="Assign Thumbnail(*)"
                                register={register}
                                name="file"
                                errors={errors}
                                selectedFile={selectedFile}
                                setSelectedFile={setSelectedFile}
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="mb-3">
                            <h6 className="mb-2">Assign Content(*)</h6>
                            <Controller
                                name="Content"
                                control={control}
                                rules = {{
                                    validate: value => {
                                        const  stripped = value.replace(/<[^>]+>/g, "").trim();
                                        return stripped.length > 0 || "Content is required.";
                                    }
                                }}
                                render={({ field }) => (
                                    <Editor
                                        value={field.value}
                                        onChange={field.onChange}
                                        errors={errors}
                                        name = "Content"
                                        disabled={isViewMode}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </form>
            </CenteredModal>
            <CenteredModal show={showDeleteModal}
                           onClose={()=> setShowDeleteModal(false)}
                           onConfirm={handleConfirmDelete}
                           title="Delete Blog Post"
            >
                <div className="text-center">
                    <span className="fs-8-1 fw-semibold">Are you sure you want to delete this private blog post?</span>
                </div>
            </CenteredModal>
        </Fragment>
    )
}
export default PrivateBlogPost;